import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from './components/Header';
import StatCard from './components/StatCard';
import ConfigPanel from './components/ConfigPanel';
import FileUpload from './components/FileUpload';
import ManualWebsiteEntry from './components/ManualWebsiteEntry';
import AnalysisProgress from './components/AnalysisProgress';
import ResultsTable from './components/ResultsTable';
import ActivityAnalysis from './components/ActivityAnalysis';
import Dashboard from './pages/Dashboard';
import Analysis from './pages/Analysis';

import Settings from './pages/Settings';
import Openers from './pages/Openers';
import LeadVerification from './pages/LeadVerification';
import VerifiedLeads from './pages/VerifiedLeads';
import ServiceWorkerTest from './components/ServiceWorkerTest';
import { analyzeWebsites } from './services/websiteAnalyzer';
import { saveAnalysisSession, getCurrentSessionId, getSessionResults, setCurrentSessionId, createNewSession } from './services/sessionService';
import './App.css';

// Import icons
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

function App() {
  // Default config - only used if no saved config exists
  const defaultConfig = {
    apiKey: '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',
    model: 'deepseek/deepseek-chat-v3-0324:free', // Set OpenRouter default model
    endpoint: 'openrouter', // Set OpenRouter as default provider
    ollamaEndpoint: 'https://llama.pressupwards.com/api/generate', // Default Ollama endpoint
    useNoThink: false, // Add useNoThink property with default value
    reoonApiKey: import.meta.env.VITE_REOON_API_KEY || 'smv1HVA2PV5X4me4m2e2dE0svhOn3WDO', // Reoon API key for email verification
    fitScoreThreshold: 6 // Default fit score threshold for opener generation
  };
  
  const [config, setConfig] = useState(defaultConfig);
  const [configLoaded, setConfigLoaded] = useState(false); // Track if config has been loaded from localStorage
  const [websites, setWebsites] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [entryMethod, setEntryMethod] = useState(''); // 'csv' or 'manual'
  const [activeTab, setActiveTab] = useState('dashboard');
  const [verifiedLeads, setVerifiedLeads] = useState([]);
  const [uploadedEmails, setUploadedEmails] = useState([]);
  const [uploadedRawData, setUploadedRawData] = useState([]);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(getCurrentSessionId());
  const [sessionName, setSessionName] = useState('');
  const [loadingSession, setLoadingSession] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [selectedService, setSelectedService] = useState('website'); // 'website', 'onboarding', 'cold_email'
  const [selectedSubService, setSelectedSubService] = useState(null); // 'apollo', 'instantly', null

  // Opener configuration for both Dashboard and Openers page
  const [openerConfig, setOpenerConfig] = useState({
    style: 'casual',
    length: 'medium',
    focus: 'refresh',
    strategy: '5line' // Default to 5line template for website redesign
  });

  // Scraping options
  const [scrapingOptions, setScrapingOptions] = useState({
    scrapeWebsite: true,
    scrapeLinkedIn: true,
    scrapeTalkingPoints: true
  });

  // Analytics metrics
  const [analyticData, setAnalyticData] = useState({
    totalWebsites: 0,
    activeAnalyses: 0,
    completedAnalyses: 0,
    recentAnalyses: 0,
    activityRate: 0
  });

  // Listen for navigation events
  useEffect(() => {
    const handleNavigate = (event) => {
      if (event.detail && event.detail.tab) {
        setActiveTab(event.detail.tab);
      }
    };
    
    window.addEventListener('navigate', handleNavigate);
    
    return () => {
      window.removeEventListener('navigate', handleNavigate);
    };
  }, []);

  // Load data from localStorage on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoadingSession(true);
      try {
        // Load config from localStorage
        const savedConfig = localStorage.getItem('analyzerConfig');
        if (savedConfig) {
          try {
            setConfig(JSON.parse(savedConfig));
            console.log('Loaded saved config from localStorage');
          } catch (e) {
            console.error('Error loading saved config:', e);
            setConfig(defaultConfig);
          }
        } else {
          console.log('No saved config found, using defaults');
          setConfig(defaultConfig);
        }
        setConfigLoaded(true); // Mark config as loaded

        // Try to load results from current session if available
        const sessionId = getCurrentSessionId();
        console.log('App.jsx: Current session ID:', sessionId);
        console.log('App.jsx: All localStorage keys:', Object.keys(localStorage));
        
        if (sessionId) {
          console.log('App.jsx: Session ID found:', sessionId);
          setCurrentSessionId(sessionId);
          
          const localSessionData = localStorage.getItem(`session_${sessionId}_data`);
          console.log('App.jsx: Local session data exists:', !!localSessionData);
          if (localSessionData) {
            try {
              const parsed = JSON.parse(localSessionData);
              console.log('App.jsx: Local session data structure:', Object.keys(parsed));
              console.log('App.jsx: Local session results count:', (parsed.results || []).length);
            } catch (e) {
              console.error('App.jsx: Error parsing local session data:', e);
            }
          }

          const sessionResults = await getSessionResults(sessionId);
          console.log('App.jsx: getSessionResults returned:', sessionResults);
          if (sessionResults && sessionResults.length > 0) {
            console.log(`App.jsx: Loaded ${sessionResults.length} results from session`);
            console.log('App.jsx: Setting results state with:', sessionResults);
            setResults(sessionResults);
            updateAnalyticData(sessionResults);
          } else {
            console.log('App.jsx: No results found in the session');
          }
        } else {
          console.log('No current session ID found');
        }

        // Load opener configuration from localStorage
        const savedOpenerConfig = localStorage.getItem('openerConfig');
        if (savedOpenerConfig) {
          try {
            setOpenerConfig(JSON.parse(savedOpenerConfig));
          } catch (e) {
            console.error('Error loading saved opener config:', e);
          }
        }
      } catch (err) {
        console.error('Error loading session data:', err);
      } finally {
        setLoadingSession(false);
      }
    };
    loadData();
  }, [debugMode]);

  // Update localStorage when config changes (but not during initial load)
  useEffect(() => {
    if (config && configLoaded) {
      console.log('Saving config to localStorage:', config);
      localStorage.setItem('analyzerConfig', JSON.stringify(config));
    }
  }, [config, configLoaded]);

  // Update opener config when service changes
  useEffect(() => {
    setOpenerConfig(prevConfig => ({
      ...prevConfig,
      serviceType: selectedService,
      subServiceType: selectedSubService
    }));
  }, [selectedService, selectedSubService]);

  // Save opener configuration to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('openerConfig', JSON.stringify(openerConfig));
  }, [openerConfig]);

  // Calculate analytics metrics based on results
  const updateAnalyticData = (analysisResults) => {
    const totalWebsites = analysisResults.length;
    const activeAnalyses = analysisResults.filter(r => r.fitScore >= 7).length;
    const completedAnalyses = analysisResults.length;
    const recentAnalyses = analysisResults.filter(r => {
      const analysisDate = new Date(r.analyzedAt || Date.now());
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return analysisDate >= weekAgo;
    }).length;

    // Calculate activity rate (% of analyses in the last week)
    const activityRate = totalWebsites > 0 ? Math.round((recentAnalyses / totalWebsites) * 100) : 0;

    setAnalyticData({
      totalWebsites,
      activeAnalyses,
      completedAnalyses,
      recentAnalyses,
      activityRate
    });
  };

  const handleConfigSave = (newConfig) => {
    setConfig(newConfig);
  };

  const handleFileUpload = (websiteData) => {
    console.log('handleFileUpload called with data:', websiteData);
    setWebsites(websiteData);
    setEntryMethod('csv');
    console.log('Websites state updated, entry method set to csv');
  };

  const handleEmailsExtracted = (emails, rawData) => {
    console.log('handleEmailsExtracted called with:', emails.length, 'emails');
    setUploadedEmails(emails);
    setUploadedRawData(rawData);
    setShowEmailVerification(true);
  };

  const handleVerificationComplete = (verificationResults) => {
    console.log('Email verification completed:', verificationResults);
    // You can add additional logic here if needed
  };

  const handleLeadsSelected = (selectedLeads) => {
    console.log('Leads selected for analysis:', selectedLeads);
    // Convert leads to website format for analysis
    const websiteData = selectedLeads.map(lead => ({
      url: lead.url || lead.website || '', // Use 'url' property to match websiteAnalyzer expectations
      company: lead.company || '',
      email: lead.email || '',
      firstName: lead.firstName || '',
      lastName: lead.lastName || '',
      linkedinUrl: lead.linkedinUrl || '', // Include LinkedIn URL
      linkedinUsername: lead.linkedinUsername || '' // Include LinkedIn username
    })).filter(item => item.url); // Only include leads with URLs
    
    if (websiteData.length > 0) {
      setWebsites(websiteData);
      setEntryMethod('csv');
      setActiveTab('dashboard'); // Switch to dashboard for analysis
    } else {
      alert('No websites found in selected leads. Please ensure your leads have website information.');
    }
  };

  const handleManualWebsites = (manualWebsites) => {
    setWebsites(manualWebsites);
    setEntryMethod('manual');
  };

  const handleStartAnalysis = async () => {
    if (!config.model || websites.length === 0) {
      alert('Please configure API settings and add websites to analyze.');
      return;
    }

    // Check if we need API key for the selected endpoint
    if ((config.endpoint === 'openai' || config.endpoint === 'openrouter' || config.endpoint === 'custom') && !config.apiKey) {
      alert('Please provide an API key for the selected provider.');
      return;
    }

    // Check for Ollama endpoint if using Ollama
    if (config.endpoint === 'ollama' && !config.ollamaEndpoint) {
      alert('Please provide an Ollama endpoint URL.');
      return;
    }

    // Check if we have a session name
    if (!sessionName && !currentSessionId) {
      alert('Please provide a session name before starting analysis.');
      return;
    }

    setIsAnalyzing(true);
    setProgress({ current: 0, total: websites.length });

    try {
      // If no current session, create one
      let sessionId = currentSessionId;
      if (!sessionId) {
        const newSession = await createNewSession(sessionName);
        if (newSession.success) {
          sessionId = newSession.sessionId;
          setCurrentSessionId(sessionId);
        } else {
          throw new Error('Failed to create new session');
        }
      }

      console.log('Running analysis with session ID:', sessionId);

      const analysisResults = await analyzeWebsites(
        websites,
        config,
        (current) => setProgress({ current, total: websites.length }),
        { 
          serviceType: selectedService, 
          subServiceType: selectedSubService, 
          focusArea: openerConfig.focus, // Map focus to focusArea for qualifyProspect
          ...openerConfig 
        },
        scrapingOptions
      );

      // Save to local storage
      console.log('Saving analysis results to session:', sessionId);
      const savedSession = await saveAnalysisSession(
        analysisResults,
        sessionName || `Analysis Session ${new Date().toLocaleDateString()}`
      );

      if (savedSession.success) {
        console.log('Successfully saved analysis to session');
        setCurrentSessionId(savedSession.sessionId);
      } else {
        console.error('Failed to save session:', savedSession.error);
      }

      // Also keep in local state
      setResults(analysisResults);
      updateAnalyticData(analysisResults);

      // Clear websites list after analysis
      setWebsites([]);
      setEntryMethod('');
      setSessionName('');
    } catch (error) {
      console.error('Analysis failed:', error);
      alert('Analysis failed. Please check your configuration and try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSessionSelect = async (sessionId, sessionResults, newSessionName = null) => {
    // If sessionId is null, it means we're creating a new session
    if (!sessionId && newSessionName) {
      console.log('Setting up for new session:', newSessionName);
      setSessionName(newSessionName);
      setResults([]);
      setWebsites([]);
      setVerifiedLeads([]);
      setCurrentSessionId(null);
      return;
    }

    // Otherwise, load the selected session
    if (sessionId) {
      console.log('Loading session:', sessionId);
      setCurrentSessionId(sessionId);
      if (sessionResults && sessionResults.length > 0) {
        console.log(`Setting ${sessionResults.length} results from session`);
        setResults(sessionResults);
        updateAnalyticData(sessionResults);
      } else {
        // If no results provided, fetch them
        try {
          console.log('Fetching results for session:', sessionId);
          const results = await getSessionResults(sessionId);
          if (results && results.length > 0) {
            console.log(`Fetched ${results.length} results`);
            setResults(results);
            updateAnalyticData(results);
          } else {
            console.log('No results found for session');
            setResults([]);
            setWebsites([]);
            setVerifiedLeads([]);
            updateAnalyticData([]);
          }
        } catch (err) {
          console.error('Error fetching session results:', err);
          setResults([]);
          setWebsites([]);
          setVerifiedLeads([]);
          updateAnalyticData([]);
        }
      }
    }
  };



  const clearAllResults = async () => {
    if (window.confirm('Are you sure you want to clear all analysis results?')) {
      setResults([]);
      setAnalyticData({
        totalWebsites: 0,
        activeAnalyses: 0,
        completedAnalyses: 0,
        recentAnalyses: 0,
        activityRate: 0
      });
    }
  };

  // Toggle debug mode with keyboard shortcut (Ctrl+Shift+D)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        setDebugMode(!debugMode);
        console.log('Debug mode:', !debugMode);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [debugMode]);

  // Filter results based on active filter
  const getFilteredResults = () => {
    if (activeFilter === 'high') {
      return results.filter(r => r.fitScore >= 7);
    } else if (activeFilter === 'low') {
      return results.filter(r => r.fitScore < 5);
    } else {
      return results;
    }
  };

  const filteredResults = getFilteredResults();

  const tabItems = [
    { id: 'dashboard', icon: 'Layout', label: 'Dashboard' },
    { id: 'lead-verification', icon: 'CheckCircle', label: 'Lead Verification' },
    { id: 'verified-leads', icon: 'Users', label: 'Verified Leads' },
    { id: 'analysis', icon: 'BarChart2', label: 'Analysis' },
    { id: 'openers', icon: 'MessageCircle', label: 'Openers' },
    { id: 'sw-test', icon: 'Cpu', label: 'SW Test' },
    { id: 'settings', icon: 'Settings', label: 'Settings' },
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false); // Close mobile menu when a tab is selected
  };

  // Render the active tab content
  const renderActiveTabContent = () => {
    if (loadingSession) {
      return (
        <div className="loading-container">
          <SafeIcon icon={FiIcons.FiRefreshCw} className="spinning" />
          <p>Loading session data...</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return (
          <Dashboard
            analyticData={analyticData}
            results={results}
            filteredResults={filteredResults}
            websites={websites}
            isAnalyzing={isAnalyzing}
            progress={progress}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
            entryMethod={entryMethod}
            handleFileUpload={handleFileUpload}
            handleManualWebsites={handleManualWebsites}
            handleStartAnalysis={handleStartAnalysis}
            handleConfigSave={handleConfigSave}
            config={config}
            clearAllResults={clearAllResults}
            sessionName={sessionName}
            setSessionName={setSessionName}
            onSessionSelect={handleSessionSelect}
            currentSessionId={currentSessionId}
            selectedService={selectedService}
            setSelectedService={setSelectedService}
            selectedSubService={selectedSubService}
            setSelectedSubService={setSelectedSubService}
            openerConfig={openerConfig}
            setOpenerConfig={setOpenerConfig}
            onEmailsExtracted={handleEmailsExtracted}
            scrapingOptions={scrapingOptions}
            setScrapingOptions={setScrapingOptions}
          />
        );
      case 'analysis':
        return <Analysis results={results} verifiedLeads={verifiedLeads} />;

      case 'openers':
        return <Openers results={results} config={config} openerConfig={openerConfig} setOpenerConfig={setOpenerConfig} scrapingOptions={scrapingOptions} />;
      case 'lead-verification':
        return <LeadVerification onLeadsSelected={handleLeadsSelected} config={config} currentSessionId={currentSessionId} />;
      case 'verified-leads':
        return <VerifiedLeads onLeadsSelected={handleLeadsSelected} currentSessionId={currentSessionId} />;

      case 'sw-test':
        return <ServiceWorkerTest />;
      case 'settings':
        return <Settings config={config} onConfigSave={handleConfigSave} clearAllResults={clearAllResults} />;
      default:
        return null;
    }
  };

  return (
    <div className="app-container">
      <div className="app-wrapper">
        <Header />
        <div className="tabs-container">
          <div
            className="hamburger-menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <SafeIcon icon={mobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="hamburger-icon" />
          </div>
          <div className={`tabs-menu ${mobileMenuOpen ? 'open' : ''}`}>
            {tabItems.map(tab => (
              <div
                key={tab.id}
                className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabClick(tab.id)}
              >
                <SafeIcon icon={`Fi${tab.icon}`} className="tab-icon" />
                <span className="tab-label">{tab.label}</span>
              </div>
            ))}
          </div>
          <div className="tab-notifications">
            {results.length > 0 && (
              <div className="notification-badge">
                <span>{results.length}</span>
              </div>
            )}
            {websites.length > 0 && (
              <div className="notification-badge">
                <span>{websites.length}</span>
              </div>
            )}
            <div className="avatar-wrapper">
              <img src="http://devserver.pressupwards.com/wp-content/uploads/2025/08/bw-david.png" alt="User avatar" className="user-avatar" />
            </div>
          </div>
        </div>

        {debugMode && (
          <div className="debug-panel">
            <h3>Debug Info</h3>
            <p>Current Session ID: {currentSessionId || 'None'}</p>
            <p>Local Storage Keys: {Object.keys(localStorage).join(', ')}</p>
            <p>Results Count: {results.length}</p>
            <p>Selected Service: {selectedService} / {selectedSubService || 'none'}</p>
            <p>Endpoint: {config.endpoint} / Model: {config.model}</p>
            <p>Ollama URL: {config.ollamaEndpoint || 'Not set'}</p>
            <p>NoThink Mode: {config.useNoThink ? 'Enabled' : 'Disabled'}</p>
            <button onClick={() => console.log('Current results:', results)}>Log Results</button>
            <button onClick={() => console.log('Current config:', config)}>Log Config</button>
            <button onClick={() => console.log('Local Storage:', localStorage)}>Log Local Storage</button>
            <button
              onClick={() => {
                localStorage.clear();
                console.log('Local Storage Cleared');
              }}
            >
              Clear Local Storage
            </button>
          </div>
        )}

        {renderActiveTabContent()}
      </div>

      <style jsx="true">{`
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 300px;
          gap: 1rem;
          color: var(--text-light);
        }
        .spinning {
          animation: spin 1.5s linear infinite;
          font-size: 2rem;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .debug-panel {
          background-color: #fff8e1;
          border: 1px solid #ffecb3;
          padding: 0.75rem;
          margin: 0.75rem;
          font-size: 0.75rem;
        }
        .debug-panel h3 {
          margin-top: 0;
          margin-bottom: 0.5rem;
          color: #b71c1c;
        }
        .debug-panel p {
          margin: 0.25rem 0;
        }
        .debug-panel button {
          margin: 0.5rem 0.5rem 0 0;
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          background: #ffecb3;
          border: 1px solid #ffd54f;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}

export default App;